import { useMemo, useRef, useState } from 'react'
import {
    ScoopMenu,
    ScoopMenuItem,
    ScoopCheckbox,
    ScoopTooltip,
    ScoopText,
    ScoopButton,
} from '../../../../ui'
import {
    CalendarDotsIcon,
    FloppyDiskIcon,
    FunnelIcon,
    GearSixIcon,
    HashIcon,
    RulerIcon,
} from '@phosphor-icons/react'
import { useExplorer } from '../../../../../context/explorer'
import styles from './DataTab.module.scss'

/**
 * Default metric template used when adding a new metric.
 */
const DEFAULT_METRIC = {
    workspaceID: '',
    metricType: 'KPI',
    derived: false,
    format: {
        formatString: '#,###',
    },
    dateKeyIndex: 0,
    period: 'Monthly',
    calendarType: 'Rolling',
    measureName: '',
    reportSeriesTableID: '',
}

/**
 * Builds a metric object from a tree node.
 * @param {Object} node - The column node from the tree
 * @returns {Object} - A metric object ready to be added to selectedItems
 */
const buildMetricFromNode = (node) => {
    return {
        ...DEFAULT_METRIC,
        workspaceID: node.workspaceID,
        measureName: node.measureName,
        reportSeriesTableID: node.reportSeriesTableID,
        format: node.format || DEFAULT_METRIC.format,
    }
}

/**
 * ColumnItem component for rendering a metric column in the tree.
 *
 * This component uses ExplorerContext via useExplorer().
 * It uses granular actions (addMetric, removeMetric) instead of the old
 * cloneDeep + setSummaryConfig pattern.
 *
 * @param {Object} props
 * @param {Object} props.node - The column node from the tree
 * @param {Function} props.onOpenDialog - Callback to open a config dialog
 */
const ColumnItem = ({ node, onOpenDialog }) => {
    const anchorRef = useRef(null)
    const [menuOpen, setMenuOpen] = useState(false)

    // Get state and actions from context
    const { dataConfig, addMetric, removeMetric } = useExplorer()

    // Find if this column is selected and get its index
    const selectedIndex = useMemo(() => {
        return dataConfig.selectedItems.findIndex(
            (item) =>
                item.measureName === node.measureName &&
                item.reportSeriesTableID === node.reportSeriesTableID &&
                !item.metricKey // Exclude saved KPIs
        )
    }, [dataConfig.selectedItems, node.measureName, node.reportSeriesTableID])

    const isSelected = selectedIndex !== -1
    const selectedMetric = isSelected ? dataConfig.selectedItems[selectedIndex] : null

    /**
     * Handles checkbox toggle for selecting/deselecting a metric.
     * Uses addMetric/removeMetric actions instead of cloneDeep pattern.
     */
    const handleCheckboxChange = (e) => {
        e.stopPropagation()

        if (isSelected) {
            // Remove metric using the granular action
            removeMetric({ index: selectedIndex })
        } else {
            // Add metric using the granular action
            const newMetric = buildMetricFromNode(node)
            addMetric(newMetric)
        }
    }

    /**
     * Counts the number of custom attributes added to the metric.
     * Used to show the badge indicator.
     */
    const getCountAttributesAdded = () => {
        if (!selectedMetric) return 0

        let count = 0
        // Format changed from default
        if (selectedMetric.format?.formatString !== selectedMetric.format?.format) count++
        // Aggregation rule set
        if (selectedMetric.aggRule) count++
        // Filter applied
        if (selectedMetric.savedFilterID) count++
        // Period shift set
        if (selectedMetric.shiftPeriod && selectedMetric.shiftPeriod !== 'Current') count++
        return count
    }

    /**
     * Opens a configuration dialog for the selected metric.
     */
    const handleOpenConfig = (dialogType) => {
        setMenuOpen(false)
        onOpenDialog(dialogType, selectedIndex)
    }

    const attributeCount = getCountAttributesAdded()

    return (
        <div className={styles.columnItem}>
            <div className={styles.columnItemLeft}>
                <ScoopCheckbox checked={isSelected} onChange={handleCheckboxChange} />
                <ScoopTooltip title={node.name}>
                    <span className={styles.columnItemTooltip}>
                        <ScoopText className={styles.columnItemName}>{node.name}</ScoopText>
                    </span>
                </ScoopTooltip>
            </div>
            <div className={styles.columnItemRight}>
                {/* Save button - shows when metric has custom attributes (future functionality) */}
                {isSelected && attributeCount > 0 && (
                    <ScoopButton
                        variant="icon"
                        size="xxs"
                        className={styles.iconButton}
                        onClick={(e) => {
                            e.stopPropagation()
                            // TODO: Implement save functionality
                        }}
                    >
                        <FloppyDiskIcon size={16} />
                    </ScoopButton>
                )}
                {/* Settings button - shows when metric is selected */}
                {isSelected && (
                    <ScoopButton
                        ref={anchorRef}
                        variant="icon"
                        size="xxs"
                        className={styles.iconButton}
                        onClick={(e) => {
                            e.stopPropagation()
                            setMenuOpen(true)
                        }}
                    >
                        {attributeCount > 0 && (
                            <span className={styles.attributeBadge}>{attributeCount}</span>
                        )}
                        <GearSixIcon size={16} />
                    </ScoopButton>
                )}
            </div>

            {/* Configuration menu */}
            <ScoopMenu
                open={menuOpen}
                anchorEl={anchorRef.current}
                onClose={() => setMenuOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                className={styles.contextMenu}
            >
                <ScoopMenuItem
                    onClick={() => handleOpenConfig('format')}
                    leftSection={
                        <span
                            className={`${styles.menuItemIndicator} ${
                                selectedMetric?.format?.formatString !==
                                selectedMetric?.format?.format
                                    ? styles.active
                                    : ''
                            }`}
                        />
                    }
                >
                    <div className={styles.menuItemContent}>
                        <HashIcon size={16} />
                        <span>Format</span>
                    </div>
                </ScoopMenuItem>
                <ScoopMenuItem
                    onClick={() => handleOpenConfig('aggregation')}
                    leftSection={
                        <span
                            className={`${styles.menuItemIndicator} ${
                                selectedMetric?.aggRule ? styles.active : ''
                            }`}
                        />
                    }
                >
                    <div className={styles.menuItemContent}>
                        <RulerIcon size={16} />
                        <span>Aggregation</span>
                    </div>
                </ScoopMenuItem>
                <ScoopMenuItem
                    onClick={() => handleOpenConfig('filter')}
                    leftSection={
                        <span
                            className={`${styles.menuItemIndicator} ${
                                selectedMetric?.savedFilterID ? styles.active : ''
                            }`}
                        />
                    }
                >
                    <div className={styles.menuItemContent}>
                        <FunnelIcon size={16} />
                        <span>Filters</span>
                    </div>
                </ScoopMenuItem>
                <ScoopMenuItem
                    onClick={() => handleOpenConfig('period')}
                    leftSection={
                        <span
                            className={`${styles.menuItemIndicator} ${
                                selectedMetric?.shiftPeriod &&
                                selectedMetric?.shiftPeriod !== 'Current'
                                    ? styles.active
                                    : ''
                            }`}
                        />
                    }
                >
                    <div className={styles.menuItemContent}>
                        <CalendarDotsIcon size={16} />
                        <span>Period</span>
                    </div>
                </ScoopMenuItem>
            </ScoopMenu>
        </div>
    )
}

export default ColumnItem
